import { Collapse } from '@components/Collapse';
import { $templateId, $tourData } from '@stores/dataStores';
import { $teamMemberData } from '@stores/memberStores';
import { $panelApi } from '@stores/panelStores';
import {
  EditConfigLanguageType,
  EditConfigLoadingType,
  type EditConfigUI,
  EnterCoverType,
  LogoType,
  PanelType,
  type TourData,
} from '@types';
import { getTemplateConfig } from '@utils/templateConfig';
import { useStore } from 'effector-react';
import whereEq from 'ramda/es/whereEq';
import { Scrollbars } from 'react-custom-scrollbars';
import PanelRootSettings from '../../panel/PanelRootSettings';
import PanelBtn from './PanelBtn';
import SidebarCustomButton from './SidebarCustomButton';

function SidebarSetting() {
  const templateId = useStore($templateId);
  const tourData = useStore($tourData);
  const teamMemberData = useStore($teamMemberData);
  const config = getTemplateConfig(templateId);

  const renderLoadingSubtitile = (type?: EditConfigLoadingType): string => {
    switch (type) {
      case EditConfigLoadingType.custom:
        return '自定义';
      case EditConfigLoadingType.text:
        return '纯文字';
      default:
        return '系统默认';
    }
  };

  const renderLanguageSubtitle = (type?: EditConfigLanguageType): string => {
    switch (type) {
      case EditConfigLanguageType.en:
        return '英文';
      case EditConfigLanguageType.system:
        return '跟随系统';
      default:
        return '中文';
    }
  };

  const renderEnterCoverSubtitile = (type?: EnterCoverType): string => {
    switch (type) {
      case EnterCoverType.image:
        return '图片开场';
      case EnterCoverType.video:
        return '视频开场';
      default:
        return '未设置';
    }
  };

  const renderLogoSubtitile = (type?: LogoType): string => {
    switch (type) {
      case LogoType.custom:
        return '自定义';
      case LogoType.disabled:
        return '不显示';
      default:
        return '系统默认';
    }
  };

  const renderUISubtitile = (ui?: EditConfigUI): string => {
    const uiColor = config('uiColor');
    if (ui === undefined || !uiColor) return '系统默认';
    return whereEq(ui, uiColor) ? '系统默认' : '自定义';
  };

  const renderFlagSubtitle = (data: Partial<TourData>): string => {
    let onCount = 0;
    let offCount = 0;
    if (data.flag?.showAuthorName === 1) {
      onCount += 1;
    } else {
      offCount += 1;
    }

    if (data.flag?.showTourPV === 1) {
      onCount += 1;
    } else {
      offCount += 1;
    }

    if (data.flag?.showSceneName === 1) {
      onCount += 1;
    } else {
      offCount += 1;
    }

    if (data.flag?.showFullscreenBtn === 1) {
      onCount += 1;
    } else {
      offCount += 1;
    }

    if (data.flag?.showVRBtn === 1) {
      onCount += 1;
    } else {
      offCount += 1;
    }

    if (data.flag?.showViewSwitchBtn === 1) {
      onCount += 1;
    } else {
      offCount += 1;
    }

    if (data.flag?.showHotspotBtn === 1) {
      onCount += 1;
    } else {
      offCount += 1;
    }

    if (data.flag?.showGyroBtn === 1) {
      onCount += 1;
    } else {
      offCount += 1;
    }

    if (data.flag?.defaultEnabledGyro === 1) {
      onCount += 1;
    } else {
      offCount += 1;
    }

    if (data.flag?.enableBouncing === 1) {
      onCount += 1;
    } else {
      offCount += 1;
    }

    if (config('globalFlagsSettings').remark) {
      if (data.flag?.showTourRemark === 1) {
        onCount += 1;
      } else {
        offCount += 1;
      }
      if (config('globalFlagsSettings').remark?.map) {
        if (data.flag?.showMap === 1) {
          onCount += 1;
        } else {
          offCount += 1;
        }
      }
    }

    if (config('globalFlagsSettings').backToFirstScene) {
      if (data.config?.firstSceneButton?.enabled === 1) {
        onCount += 1;
      } else {
        offCount += 1;
      }
    }

    if (data.flag?.forceLandscape === 1) {
      onCount += 1;
    } else {
      offCount += 1;
    }

    if (data.flag?.compareMode === 1) {
      onCount += 1;
    } else {
      offCount += 1;
    }

    return `${onCount}开${offCount}关`;
  };

  const renderInteractiveFlagSubtitle = (data: Partial<TourData>): string => {
    let onCount = 0;
    let offCount = 0;
    if (data.flag?.showShareBtn === 1) {
      onCount += 1;
    } else {
      offCount += 1;
    }

    if (data.flag?.showLikeBtn === 1) {
      onCount += 1;
    } else {
      offCount += 1;
    }

    if (data.flag?.showCommentBtnV2 === 1) {
      onCount += 1;
    } else {
      offCount += 1;
    }

    return `${onCount}开${offCount}关`;
  };

  const renderBaseInfo = (title: string) => (
    <Collapse title={title}>
      <PanelBtn
        title="作品信息"
        onClick={() => $panelApi.show({ type: PanelType.info })}
      />
    </Collapse>
  );

  const renderOpeningSettings = (title: string) => (
    <Collapse title={title}>
      {config('openingLoading') && (
        <PanelBtn
          title="页面加载样式"
          showVip
          subtitle={renderLoadingSubtitile(tourData.config?.loading?.type)}
          onClick={() => $panelApi.show({ type: PanelType.loading })}
        />
      )}
      <PanelBtn
        title="开场封面"
        showVip
        subtitle={renderEnterCoverSubtitile(tourData.enterCover?.type)}
        onClick={() => $panelApi.show({ type: PanelType.enterCover })}
      />
      {config('openingSandtable') && (
        <PanelBtn
          title="开场沙盘"
          showVip
          subtitle={
            (tourData.config?.sandBox?.list?.length ?? 0) > 0
              ? '已设置'
              : '未设置'
          }
          onClick={() => $panelApi.show({ type: PanelType.sandTable })}
        />
      )}
      <PanelBtn
        title="开场动画"
        subtitle={
          (tourData.startAnime?.pano ?? 0) > 0 ||
          (tourData.startAnime?.matrix ?? 0) > 0 ||
          (tourData.startAnime?.video ?? 0) > 0
            ? '开启'
            : '关闭'
        }
        onClick={() => $panelApi.show({ type: PanelType.startAnime })}
      />
      <PanelBtn
        title="开场提示"
        subtitle={tourData.tourTip?.enabled === 0 ? '关闭' : '开启'}
        onClick={() => $panelApi.show({ type: PanelType.tourTip })}
      />
    </Collapse>
  );

  const renderUISetting = (title: string) => (
    <Collapse title={title}>
      <PanelBtn
        title="自定义LOGO"
        showVip
        subtitle={renderLogoSubtitile(tourData.logo?.type)}
        onClick={() => $panelApi.show({ type: PanelType.logo })}
      />
      <PanelBtn
        title="场景选择"
        subtitle={
          tourData.categoryConfig?.enabledButton &&
          tourData.categoryConfig.enabledColumn
            ? '开启'
            : '关闭'
        }
        onClick={() => $panelApi.show({ type: PanelType.categoryConfig })}
      />
      <PanelBtn
        title="全局开关"
        subtitle={renderFlagSubtitle(tourData)}
        onClick={() => $panelApi.show({ type: PanelType.globalSettings })}
      />
      <PanelBtn
        title="互动功能"
        subtitle={renderInteractiveFlagSubtitle(tourData)}
        onClick={() => $panelApi.show({ type: PanelType.interactive })}
      />
      {config('searchSetting') && (
        <PanelBtn
          title="搜索设置"
          subtitle={tourData.config?.search?.enabled ?? false ? '开启' : '关闭'}
          showVip
          onClick={() => $panelApi.show({ type: PanelType.search })}
        />
      )}
      {config('copyright') && (
        <PanelBtn
          title="免责声明"
          subtitle={tourData.config?.copyright?.content ? '已设置' : '未设置'}
          onClick={() => $panelApi.show({ type: PanelType.copyright })}
        />
      )}
      {config('language') && (
        <PanelBtn
          title="界面语言"
          showVip
          subtitle={renderLanguageSubtitle(tourData.config?.language?.type)}
          onClick={() => $panelApi.show({ type: PanelType.language })}
        />
      )}
      {!!config('uiColor') && (
        <PanelBtn
          title="界面配色"
          subtitle={renderUISubtitile(tourData.config?.ui)}
          showVip
          onClick={() => $panelApi.show({ type: PanelType.uiColor })}
        />
      )}
    </Collapse>
  );

  const renderMarketingSetting = (title: string) => {
    if (teamMemberData.role !== 0) return;
    return (
      <Collapse title={title}>
        <PanelBtn
          title="付费观看设置"
          showVip
          subtitle={tourData.config?.vipView?.enabled ? '已设置' : '未设置'}
          onClick={() => $panelApi.show({ type: PanelType.vipView })}
        />
      </Collapse>
    );
  };

  return (
    <Scrollbars>
      {renderBaseInfo('基础信息')}
      {renderOpeningSettings('开场设置')}
      {renderUISetting('界面设置')}
      {renderMarketingSetting('营销设置')}
      {config('customButton') > 0 && <SidebarCustomButton />}
      <PanelRootSettings />
    </Scrollbars>
  );
}

export default SidebarSetting;
